/* eslint-disable security/detect-object-injection */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { IUserInfo } from './post-localization.action';
import { LOCALIZATION_DEFAULT_LOCALE } from '../configuration/localization';
import { ensureAuthenticated } from '../utilities/is-authenticated';

/**
 * GetLocalization Input Action
 */
export class GetLocalizationInput implements Msdyn365.IActionInput {
    public timestamp: string;
    public userToken: string;
    constructor(args: Msdyn365.ICreateActionContext) {
        this.timestamp = new Date().getTime().toString();
        this.userToken = args.requestContext.user.token;
    }

    public getCacheKey = () => `GetLocalizationKey-${this.timestamp}-${this.userToken}`;
    public getCacheObjectType = () => 'GetLocalization';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

export interface IGetLocalizationData {
    numberLocale: INumberFormat;
    dateLocale: string;
}

export interface INumberFormat {
    thousandSeparator: Separator;
    decimalSeparator: Separator;
}

export enum Separator {
    Dot = '.',
    Comma = ','
}

export enum NumberLocale {
    DE = '###.###,000',
    EN = '###,###.000'
}

export const numberFormats: { [key: string]: INumberFormat } = {
    'en-US': { thousandSeparator: Separator.Comma, decimalSeparator: Separator.Dot },
    'de-DE': { thousandSeparator: Separator.Dot, decimalSeparator: Separator.Comma }
};

export const mapNumberFormatToLocale = (numberFormat: INumberFormat): string => {
    if (numberFormat.thousandSeparator === Separator.Dot && numberFormat.decimalSeparator === Separator.Comma) {
        return 'de-DE';
    } else if (numberFormat.thousandSeparator === Separator.Comma && numberFormat.decimalSeparator === Separator.Dot) {
        return 'en-US';
    } else {
        throw new Error('Unsupported number format');
    }
};

export function getNumberFormatByLocale(locale: string): INumberFormat | undefined {
    const localeMap: { [key: string]: string } = {
        EN: 'en-US',
        DE: 'de-DE'
    };

    const mappedLocale = localeMap[locale];
    return numberFormats[mappedLocale];
}

export const separatorFormats: { [key in Separator]: INumberFormat } = {
    [Separator.Comma]: { thousandSeparator: Separator.Dot, decimalSeparator: Separator.Comma },
    [Separator.Dot]: { thousandSeparator: Separator.Comma, decimalSeparator: Separator.Dot }
};

export enum DateLocale {
    DE = 'de-DE',
    US = 'en-US',
    GB = 'en-GB'
}

const dateFormats: { [key in DateLocale]: string } = {
    [DateLocale.DE]: 'dd.mm.yyyy',
    [DateLocale.US]: 'mm/dd/yyyy',
    [DateLocale.GB]: 'dd/mm/yyyy'
};

export const getNumberFormat = (locale: NumberLocale | Separator): INumberFormat => {
    return numberFormats[locale];
};

export const getDateFormat = (locale: DateLocale): string => {
    return dateFormats[locale];
};

const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new GetLocalizationInput(args);
};

async function action(_: GetLocalizationInput, ctx: Msdyn365.IActionContext): Promise<IGetLocalizationData> {
    if (!ctx.requestContext.params.isEditor) {
        ensureAuthenticated(ctx);
    }
    const userInfo: IGetLocalizationData = {} as IGetLocalizationData;
    await Msdyn365.sendRequest(
        `${ctx.requestContext.apiSettings.baseUrl}Commerce/GetUserInfo`,
        'get',
        {},
        {
            headers: {
                OUN: ctx.requestContext.apiSettings.oun,
                Authorization: `id_token ${ctx.requestContext.user.token}`,
                'Accept-Language': LOCALIZATION_DEFAULT_LOCALE
            }
        }
    )
        .then(res => {
            const responseData = res.data as IUserInfo;
            userInfo.dateLocale = responseData.DateFormat;
            const numberFormat = numberFormats[responseData.NumberFormat];
            userInfo.numberLocale = numberFormat;
        })
        .catch(_ => {
            return {} as IGetLocalizationData;
        });
    return { numberLocale: userInfo.numberLocale, dateLocale: userInfo.dateLocale };
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetLocalizationData>>action,
    id: 'GetLocalization',
    input: createInput
});
